// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable i18next/no-literal-string */
import { COUNTRY_LANGUAGES, DEFAULT_COUNTRY } from '@src/config/localisation/locale-config.constants'
import { LOCALE_COOKIE } from '@src/config/localisation/locale-config.static-constants'
import type { TAbsoluteLocale } from '@src/config/localisation/locale-config.types'
import { getIsCountryCompliant, getLocaleConfigFromMemory } from '@src/modules/global-state/locale-config.browser'
import { USER_CONSENT_COOKIES, ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import eraseCookie from '@src/quince-site-common/src/utils/eraseCookie'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import mixpanelCl from '@src/telemetry/integrations/mixpanel'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { trackInternalEvent } from '@src/telemetry/track'
import { parseLocale } from '@src/utils/i18n.utils'

import { initializeScript } from '../provider-utils/initialize-script'

import {
  QUINCE_CONSENT_DECISION,
  QUINCE_CONSENT_EVENT,
  QuinceConsentGrantedDetail,
  TRUSTARC_DATALAYER_EVENT,
  TRUSTE_CONSENT_DECISION,
  TrustArcDataLayerEvent,
} from './trustarc.type'

export let isTrustARCScriptLoading = true
export let resolveTrustArcInitialisePromise: (value: unknown) => void
export const initialiseTrustARCPromise = new Promise(resolve => {
  resolveTrustArcInitialisePromise = resolve
})

const TRUSTARC_READY_EVENT = 'trustarc-ccm-ready'
const TRUSTARC_CLOSE_TOPIC = 'close'
const TRUSTARC_READY_LISTENER_KEY = '__quinceTrustArcReadyListenerAttached'
const TRUSTARC_CLOSE_LISTENER_KEY = '__quinceTrustArcCloseListenerAttached'
const TRUSTARC_MUTATION_OBSERVER_KEY = '__quinceTrustArcMutationObserverAttached'
const TRUSTARC_DATALAYER_LISTENER_KEY = '__quinceTrustArcDataLayerListenerAttached'

/**
 * Parses getGDPRConsentDecision result into functional/advertising booleans.
 * Per TrustArc docs (v1.5):
 *   [0]     = no preference set
 *   [1]     = Required only
 *   [1,2]   = Required + Functional
 *   [1,2,3] = Required + Functional + Advertising (Accept All)
 *   [1,3]   = Required + Advertising (no Functional)
 */
const parseConsentDecision = (consentDecision: TRUSTE_CONSENT_DECISION[]) => {
  const isFunctional = consentDecision.includes(TRUSTE_CONSENT_DECISION.FUNCTIONAL)
  const isAdvertising = consentDecision.includes(TRUSTE_CONSENT_DECISION.ADVERTISING)
  return { isFunctional, isAdvertising }
}

/**
 * Debounce guard — prevents double-firing if both dataLayer and close listener
 * fire within 500ms for the same consent action.
 */
let cookiePreferenceSavedFiredAt = 0
const COOKIE_PREFERENCE_DEBOUNCE_MS = 500

const fireCookiePreferenceSaved = (isFunctional: boolean, isAdvertising: boolean) => {
  const now = Date.now()
  if (now - cookiePreferenceSavedFiredAt < COOKIE_PREFERENCE_DEBOUNCE_MS) return
  cookiePreferenceSavedFiredAt = now

  writeCookieConsentValues(isFunctional, isAdvertising)

  trackInternalEvent(TELEMETRY_EVENT_NAMES.COOKIE_PREFERENCE_SAVED, {
    context: {
      functional_cookie_accepted: isFunctional,
      advertising_cookie_accepted: isAdvertising,
    },
  })

  // Banner path: dynamically inject 3P scripts so the current page activates
  // without a reload. Every subsequent SSR request will include them natively
  // (suppressThirdPartyScripts=false once user_advertising_consent=true cookie is set).
  // Modal path: reload is still handled by the CLOSE button (mutation observer).
  if (!isModalPath) {
    const detail: QuinceConsentGrantedDetail = { isAdvertising, isFunctional }
    window.dispatchEvent(new CustomEvent(QUINCE_CONSENT_EVENT.GRANTED, { detail }))
    // scheduleReload() — replaced by dynamic script injection; reload no longer needed for banner flow
  }
}

/**
 * Listens for TrustArc's official dataLayer event `trustarc-consent-updated`.
 * Fires when user updates consent via banner or modal — Accept All, Reject All, or Save.
 * Official TrustArc-recommended approach per CM API Integration Guide v1.5.
 *
 * Requires "Push CCM events to dataLayer" enabled in TrustArc settings.
 * Payload: { event, timestamp, consent: { ta_category_1, ta_category_2, ta_category_3 }, consentModel }
 *   ta_category_1 = Required, ta_category_2 = Functional, ta_category_3 = Advertising
 */
export const attachTrustArcDataLayerListener = () => {
  if (typeof window === 'undefined') return
  const win = window as typeof window & Record<string, boolean>
  if (win[TRUSTARC_DATALAYER_LISTENER_KEY]) return
  win[TRUSTARC_DATALAYER_LISTENER_KEY] = true

  const dl = window as typeof window & { dataLayer?: unknown[] }
  dl.dataLayer = dl.dataLayer ?? []

  const origPush = dl.dataLayer.push.bind(dl.dataLayer)
  dl.dataLayer.push = function (...args: unknown[]) {
    args.forEach(entry => {
      try {
        const event = entry as TrustArcDataLayerEvent

        // trustarc-ccm-ready with all undefined consent = new user, banner shown
        if (event?.event === TRUSTARC_DATALAYER_EVENT.READY) {
          const consent = event.consent ?? {}
          const allUndefined = Object.values(consent).every(v => v === undefined)
          if (allUndefined) {
            trackInternalEvent(TELEMETRY_EVENT_NAMES.COOKIE_BANNER_SHOWN, {})
          }
        }

        if (event?.event === TRUSTARC_DATALAYER_EVENT.CONSENT_UPDATED) {
          const consent = event.consent ?? {}
          const isFunctional = consent.ta_category_2 === true
          const isAdvertising = consent.ta_category_3 === true

          fireCookiePreferenceSaved(isFunctional, isAdvertising)
        }
      } catch (err) {
        console.error('[TrustArc] Error handling dataLayer event', err)
      }
    })
    return origPush(...args)
  }
}

/**
 * Writes user_functional_consent and user_advertising_consent cookies.
 * When advertising consent is revoked, clears all 3P tracking cookies and
 * calls SDK opt-out methods. Cleanup is UK-only (countryCompliant locales) —
 * US/CA manage their own consent flow separately.
 */
const writeCookieConsentValues = (isFunctional: boolean, isAdvertising: boolean) => {
  // Capture the PREVIOUS values before overwriting, so we only rotate identity on an actual
  // transition (grant / revoke) — not on every page-load consent re-sync.
  const currentFunctional = getCookie(USER_CONSENT_COOKIES.FUNCTIONAL)
  const currentAdvertising = getCookie(USER_CONSENT_COOKIES.ADVERTISING)
  const prevAdvertising = currentAdvertising === 'true'

  const functionalValue = String(isFunctional)
  const advertisingValue = String(isAdvertising)

  // Write-on-change: if both cookies already hold the target values, there is nothing to do —
  // skip the writes and the (transition-only) identity rotation / cleanup below.
  if (currentFunctional === functionalValue && currentAdvertising === advertisingValue) return

  const maxAge = 365 * 24 * 60 * 60 // 1 year
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  if (currentFunctional !== functionalValue) {
    document.cookie = `${USER_CONSENT_COOKIES.FUNCTIONAL}=${isFunctional}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`
  }
  if (currentAdvertising !== advertisingValue) {
    document.cookie = `${USER_CONSENT_COOKIES.ADVERTISING}=${isAdvertising}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`
  }

  // Identity rotation + 3P cookie cleanup is UK-only — US/CA manage consent separately.
  if (!getIsCountryCompliant()) return

  const granted = isAdvertising && !prevAdvertising
  const revoked = !isAdvertising && prevAdvertising

  // GRANT (false→true): rotate the device id (D2) so post-consent data — and,
  // once present, lb_user_id and (if logged in) the customer $user_id — is a clean
  // identity, unlinkable from the pre-consent anonymous device (D1). We deliberately
  // do NOT rotate the session id: session continuity is preserved across the consent
  // boundary (this matches pre-UK behaviour, where consent never rotated the session).
  // Runs on BOTH consent paths (no-reload banner and modal/reload), since
  // writeCookieConsentValues is called on each.
  if (granted) {
    mixpanelCl.rotateDeviceId()
    return
  }

  if (revoked) {
    // Mixpanel — clear the tentative-email cookie (PII) on revoke. For UK the browser
    // SDK is disabled (ssr_mixpanel_sdk_enabled=false) so it is usually already empty,
    // but erase defensively. The device id is rotated at the end of this block.
    eraseCookie(ANALYTIC_COOKIES.MIXPANEL_TENTATIVE_EMAIL)

    // Google — no in-page Consent Mode call is needed. Revoke is reached via the
    // Cookie Settings modal, which reloads (scheduleReload); the post-reload SSR omits
    // the GTM web container entirely once user_advertising_consent=false, so the
    // container (and its Ads/remarketing tags) is gone. We only need to delete the
    // first-party cookies it already set, since a reload does not remove them.
    // Google cookies — manual deletion (no SDK clear method exists)
    // _ga and _gcl_au are known exact names (first-party, safe to delete)
    eraseCookie(ANALYTIC_COOKIES.THIRD_PARTY.GOOGLE_GA_ID)
    eraseCookie(ANALYTIC_COOKIES.THIRD_PARTY.GOOGLE_GCID)
    // _ga_* wildcard scan commented out pending review — these are first-party
    // cookies set on our domain but the wildcard pattern needs sign-off before enabling.
    // document.cookie.split(';').forEach(cookie => {
    //   const name = cookie.split('=')[0].trim()
    //   if (name.startsWith(ANALYTIC_COOKIES.THIRD_PARTY.GOOGLE_GA4_ID_PREFIX)) {
    //     eraseCookie(name)
    //   }
    // })

    // Meta — revoke consent (stops future pixel fires) + delete cookies
    window.fbq?.('consent', 'revoke')
    eraseCookie(ANALYTIC_COOKIES.THIRD_PARTY.FACEBOOK_FBP)
    eraseCookie(ANALYTIC_COOKIES.THIRD_PARTY.FACEBOOK_FBC)

    // TikTok
    eraseCookie(ANALYTIC_COOKIES.THIRD_PARTY.TIKTOK_PIXEL)

    // Impact — first-party affiliate click id we persist from the `irclickid` URL
    // param (and the SGTM response header). Impact is UK-permitted post-consent, so
    // clear it on revoke. (The Impact SDK's own cookies are third-party and managed
    // by the SDK; only our first-party impactClickId is in our control.)
    eraseCookie(ANALYTIC_COOKIES.IMPACT_CLICK_ID)

    // Attentive — wildcard scan for any cookie with one or more leading
    // underscores followed by "attentive" (SDK cookies are double-underscore:
    // __attentive_id, __attentive_cco, __attentive_session_id, __attentive_ss_referrer
    // etc.) plus the _attn_ identity cookie. NOTE: the previous check used
    // `startsWith('_attentive')` which never matched the real (double-underscore)
    // names — `/^_+attentive/` matches single OR double underscore prefixes.
    // Also clear the `attntv_*` checkout cookies — `attntv_mstore_email` and
    // `attntv_mstore_phone` hold the visitor's RAW EMAIL / PHONE (PII). They don't
    // match the `_attentive` SDK prefix, so match the `attntv_` prefix explicitly;
    // otherwise revoked UK users keep their email/phone in cookies until expiry.
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim()
      if (/^_+attentive/.test(name) || name.startsWith('attntv_') || name === '_attn_') {
        eraseCookie(name)
      }
    })

    // Mixpanel device ID — rotate to a fresh anonymous UUID so the revoked
    // (post-consent) device ID cannot be linked to any future events. rotateDeviceId
    // overwrites the cookie (the overwrite is the erase) and is cookie-only, so it is
    // safe with the browser SDK disabled for UK. $user_id is already null at this point
    // because user_advertising_consent is now "false".
    mixpanelCl.rotateDeviceId()

    // Session id is intentionally NOT rotated on revoke. Device-id rotation above
    // already breaks linkability of future events, and $lb_user_id is null in the
    // SGTM payload post-revoke (gated on advertising consent in getCommonProperties).
    // Pre-UK behaviour never rotated the session on a consent transition; we keep that.
  }
}

/**
 * Tracks whether the user is currently in the modal (Cookie Settings) path.
 * Set to true when the TrustArc overlay appears, false when it's removed.
 * Used to suppress reload on trustarc-consent-updated for modal path —
 * reload is handled by the close button (mutation observer) instead.
 */
let isModalPath = false

/** Guard so that two close paths (eu.addEventListener + mutation observer) never double-reload */
let reloadScheduled = false

const scheduleReload = () => {
  // Reload on the Cookie Settings modal-close path for ALL countries. The consent
  // cookies are persisted by writeCookieConsentValues (which runs for every country
  // inside saveConsentDecisionCookies) before this reload, so the post-reload SSR
  // pass renders with the user's latest consent. For UK this is what injects/strips
  // the SSR-suppressed 3P scripts; for US/CA scripts always load regardless, so the
  // reload simply re-renders with the freshly written consent cookies.
  // NOTE: only the modal-close path calls scheduleReload — the banner Accept/Reject
  // path never reloads (it uses live script injection), so this does not reload on
  // every banner interaction.
  if (reloadScheduled) return
  reloadScheduled = true
  window.location.reload()
}

const TRUSTARC_CMA_READY_TIMEOUT_MS = 10000

/**
 * Polls until window.truste.cma is available.
 * Resolves after timeoutMs regardless to avoid hanging indefinitely.
 */
const waitForTrusteCMAReady = (timeoutMs = TRUSTARC_CMA_READY_TIMEOUT_MS): Promise<void> =>
  new Promise(resolve => {
    if (typeof window.truste?.cma !== 'undefined') {
      resolve()
      return
    }
    const timeout = setTimeout(resolve, timeoutMs)
    const interval = setInterval(() => {
      if (typeof window.truste?.cma !== 'undefined') {
        clearInterval(interval)
        clearTimeout(timeout)
        resolve()
      }
    }, 50)
  })

/**
 * Reads the current TrustArc GDPR consent decision and persists it into two
 * first-party cookies so the rest of the app can gate features without waiting
 * for the TrustArc SDK:
 *   user_functional_consent  = "true" | "false"
 *   user_advertising_consent = "true" | "false"
 *
 * "Has the user decided yet?" is detected purely from the SDK decision — no TrustArc
 * cookie name is read. `getGDPRConsentDecision()` returns `[0]` (NOT_ACTED = "no preference
 * set") when no choice has been made — distinct from an explicit reject-all (which includes
 * REQUIRED). On no preference we never write a misleading value; instead non-compliant locales
 * (US/CA) imply consent and default to true (when absent), while compliant locales (UK) stay
 * strictly gated and write nothing.
 */
export const saveConsentDecisionCookies = async (): Promise<void> => {
  try {
    if (typeof window === 'undefined') return

    await waitForTrusteCMAReady()

    const domain = getTrustArcDomain()
    const { consentDecision } = window.truste.cma.callApi<{ consentDecision: TRUSTE_CONSENT_DECISION[] }>(
      'getGDPRConsentDecision',
      domain,
    )

    // "Has the user decided?" — `[0]` (QUINCE_CONSENT_DECISION.NOT_ACTED) is TrustArc's "no
    // preference set" sentinel; any real decision contains REQUIRED and never includes it.
    // Replaces the old `<cookieName>_gdpr` cookie read with the documented decision-array value.
    const hasDecided = !consentDecision.includes(QUINCE_CONSENT_DECISION.NOT_ACTED)
    const countryCompliant = getIsCountryCompliant()
    const { isFunctional, isAdvertising } = hasDecided
      ? parseConsentDecision(consentDecision)
      : { isFunctional: !countryCompliant, isAdvertising: !countryCompliant }
    if (!hasDecided) {
      // No decision in the CURRENT region. Write that region's implied default and OVERWRITE any
      // value carried over from another region — our consent cookies are stored site-wide (unlike
      // TrustArc's per-domain decision), so a UK value must not linger on US/CA, or vice-versa.
      // US/CA imply consent (true); UK is strict opt-in (false). A real choice is never [0], so this
      // never overrides an actual decision; write-on-change keeps it a no-op when unchanged.
      const impliedConsent = !countryCompliant
      writeCookieConsentValues(impliedConsent, impliedConsent)
      return
    }

    writeCookieConsentValues(isFunctional, isAdvertising)
  } catch (error) {
    console.error('Error saving consent decision cookies', error)
  }
}

/**
 * DOM-based close detector.
 * Phase 1: watches for TrustArc overlay to appear in DOM.
 * Phase 2: watches for overlay removal → saves cookies → reloads.
 * Covers X button and CLOSE CTA on confirmation screen.
 */
const attachTrustArcMutationObserverCloseListener = () => {
  if (typeof MutationObserver === 'undefined') return
  if (!document.documentElement) return

  const docEl = document.documentElement as HTMLElement & Record<string, boolean>
  if (docEl[TRUSTARC_MUTATION_OBSERVER_KEY]) return
  docEl[TRUSTARC_MUTATION_OBSERVER_KEY] = true

  const outerObserver = new MutationObserver(() => {
    const overlayId = window.truste?.eu?.popdiv
    if (!overlayId) return

    const overlay = document.getElementById(overlayId)
    if (!overlay) return

    // Overlay appeared — user is in modal path
    isModalPath = true
    outerObserver.disconnect()

    const innerObserver = new MutationObserver(() => {
      if (document.body && !document.body.contains(overlay)) {
        // Overlay removed — modal closed, reset flag and reload
        isModalPath = false
        void saveConsentDecisionCookies().then(() => {
          scheduleReload()
        })
        innerObserver.disconnect()
      }
    })

    innerObserver.observe(document.documentElement, { childList: true, subtree: true })
  })

  outerObserver.observe(document.documentElement, { childList: true, subtree: true })
}

const attachTrustArcCloseListener = () => {
  const trustArcApi = window.truste?.eu as (typeof window.truste.eu & Record<string, boolean>) | undefined
  if (!trustArcApi?.addEventListener) return
  if (trustArcApi[TRUSTARC_CLOSE_LISTENER_KEY]) return

  trustArcApi.addEventListener(topic => {
    if (topic === TRUSTARC_CLOSE_TOPIC) {
      void saveConsentDecisionCookies().then(() => {
        scheduleReload()
      })
    }
  })

  trustArcApi[TRUSTARC_CLOSE_LISTENER_KEY] = true
}

const addTrustArcReadyListenerToBody = () => {
  const body = document.body as (typeof document.body & Record<string, boolean>) | null
  if (!body) return false
  if (body[TRUSTARC_READY_LISTENER_KEY]) return false

  body.addEventListener(TRUSTARC_READY_EVENT, attachTrustArcCloseListener)
  body[TRUSTARC_READY_LISTENER_KEY] = true
  return true
}

export const attachTrustArcCloseReloadListener = () => {
  if (typeof document === 'undefined') return

  if (addTrustArcReadyListenerToBody()) return

  if (typeof MutationObserver === 'undefined') return
  if (!document.documentElement) return
  if (typeof Node !== 'undefined' && !(document.documentElement instanceof Node)) return

  const observer = new MutationObserver(() => {
    if (addTrustArcReadyListenerToBody()) {
      observer.disconnect()
    }
  })

  observer.observe(document.documentElement, { childList: true })
}

export const getTrustArcLanguageCode = (): string => {
  try {
    const locale = getCookie(LOCALE_COOKIE)
    if (locale) {
      const { language, country } = parseLocale(locale as TAbsoluteLocale)
      return `${language}_${country?.toLowerCase()}`
    }

    return COUNTRY_LANGUAGES[DEFAULT_COUNTRY][0]
  } catch (error) {
    console.error('Error getting TrustArc language code:', error)
    return COUNTRY_LANGUAGES[DEFAULT_COUNTRY][0]
  }
}

// @i18n-ignore
const DEFAULT_TRUSTARC_DOMAIN = 'quince.com'
// @i18n-ignore
const DEFAULT_TRUSTARC_COOKIE_NAME = 'quince_us'

export const getTrustArcDomain = (): string => {
  try {
    return getLocaleConfigFromMemory().trustArcDomain ?? DEFAULT_TRUSTARC_DOMAIN
  } catch (error) {
    console.error('Error getting TrustArc domain:', error)
    return DEFAULT_TRUSTARC_DOMAIN
  }
}

export const getTrustArcCookieName = (): string => {
  try {
    return getLocaleConfigFromMemory().trustArcCookieName ?? DEFAULT_TRUSTARC_COOKIE_NAME
  } catch (error) {
    console.error('Error getting TrustArc cookie name:', error)
    return DEFAULT_TRUSTARC_COOKIE_NAME
  }
}

export const TRUSTARC_CONFIG: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
  },
  initialize: async () => {
    const domain = getTrustArcDomain()
    const isNonDefaultDomain = domain !== DEFAULT_TRUSTARC_DOMAIN

    // Primary: official TrustArc dataLayer consent-updated event
    attachTrustArcDataLayerListener()
    // Secondary: mutation observer watches for overlay removal → saves cookies → reloads
    attachTrustArcMutationObserverCloseListener()

    await initializeScript({
      key: 'trustarc',
      domain: 'https://consent.trustarc.com',
      path: () => {
        const cookieName = getTrustArcCookieName()
        const languageCode = getTrustArcLanguageCode()
        if (isNonDefaultDomain) {
          return `/notice?domain=${domain}&c=teconsent&js=nj&noticeType=bb&text=true&gtm=1&cookieName=${cookieName}&language=${languageCode}`
        }
        return `/notice?domain=${domain}&c=teconsent&js=nj&noticeType=bb&gtm=1&language=${languageCode}`
      },
      injectAt: 'beforeFirstScript',
      onLoad: () => {
        resolveTrustArcInitialisePromise(null)
        isTrustARCScriptLoading = false

        void waitForTrusteCMAReady().then(() => {
          attachTrustArcCloseListener()

          // Sync consent decision for returning visitors
          void saveConsentDecisionCookies()
        })
      },
    })
  },
}
